// src/components/Story/AllCategory.jsx
import React, { memo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import config from "../../config";
import { ImageOff } from 'lucide-react';

const GridIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
    />
  </svg>
);

const ListIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

const AllCategory = memo(({ filterData, loading }) => {
  const [viewMode, setViewMode] = useState("grid"); // "grid" | "list"
  const isGrid = viewMode === "grid";

  const containerClass = isGrid
    ? "grid grid-cols-1 min-sm:grid-cols-2 min-lg:grid-cols-3 min-xl:grid-cols-4 gap-6 min-md:gap-8"
    : "flex flex-col min-md:grid gap-5 min-md:grid-cols-2";

  const cardClass = isGrid
    ? "group relative overflow-hidden rounded-2xl bg-white shadow-md hover:shadow-2xl transition-all duration-500"
    : "group relative flex flex-row gap-6 bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500 p-5";

  const imageWrapperClass = isGrid
    ? "relative aspect-[1.3] overflow-hidden rounded-t-2xl"
    : "relative w-48 min-sm:w-56 min-md:w-64 min-lg:w-72 min-xl:w-80 shrink-0 overflow-hidden rounded-2xl";

  return (
    <section className="py-12 min-md:py-8 bg-gray-50/60 mb-8">
      <div className="mx-auto px-5 min-xl:px-8 min-xl:w-4/5">
        {/* En-tête + switch vue */}
        <div className="flex flex-col min-sm:flex-row justify-between items-start min-sm:items-center mb-10 min-md:mb-14 gap-6">
          <h2 className="text-3xl min-md:text-4xl font-serif font-semibold text-gray-900 tracking-tight">
          </h2>

          <div className="flex items-center gap-2 bg-white rounded-full shadow-sm border border-gray-200 p-1.5">
            <button
              aria-label="Vue en grille"
              onClick={() => setViewMode("grid")}
              className={`p-2.5 rounded-full transition-all ${
                isGrid
                  ? "bg-gray-900 text-white shadow-md"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <GridIcon />
            </button>
            <button
              aria-label="Vue en liste"
              onClick={() => setViewMode("list")}
              className={`p-2.5 rounded-full transition-all ${
                !isGrid
                  ? "bg-gray-900 text-white shadow-md"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <ListIcon />
            </button>
          </div>
        </div>

        {loading ? (
          <div className={containerClass}>
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="animate-pulse bg-gray-200 rounded-2xl"
                style={{ aspectRatio: isGrid ? "4 / 5" : "auto", height: isGrid ? "auto" : "180px" }}
              />
            ))}
          </div>
        ) : filterData.length === 0 ? (
          <div className="text-center py-24 text-gray-500">
            <p className="text-xl">Aucune catégorie disponible pour le moment</p>
          </div>
        ) : (
          <ul className={containerClass}>
            {filterData.map((item) => {
              const hasValidImage = item.picture && item.picture !== "/picture/category/";

              return (
                <li key={item._id}>
                  <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.7, ease: "easeOut" }}
                  >
                    <Link
                      to={`/product-by-category/${item._id}`}
                      className="block focus:outline-none focus:ring-2 focus:ring-gray-400/50 rounded-2xl"
                    >
                      <div className={cardClass}>
                        {/* Image */}
                        <div className={imageWrapperClass}>
  {hasValidImage ? (
    <>
      <img
        src={`${config.REACT_APP_API_URL}${item.picture}`}
        alt={item.name || "Catégorie"}
        className={`${isGrid ? "h-full" : "h-64"} w-full  object-cover transition-transform duration-700 group-hover:scale-110`}
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl" />
    </>
  ) : (
    <div className={`${isGrid ? "h-full" : "h-64"} w-full bg-gray-50 flex flex-col items-center justify-center text-gray-400`}>
      <ImageOff className="w-20 h-20 mb-4 opacity-60" strokeWidth={1.4} />
      <p className="text-sm font-medium">Image non disponible</p>
    </div>
  )}

  {/* Badge catégorie reste visible par-dessus */}
  {isGrid && item.category?.name && (
    <span className="absolute top-4 left-4 bg-white/90 backdrop-blur-md text-gray-800 text-xs font-semibold px-3 py-1.5 rounded-full shadow z-10">
      {item.category.name}
    </span>
  )}
</div>

                        {/* Contenu texte */}
                        <div className={isGrid ? "p-6 text-center" : "flex-1 py-2 pr-4 flex flex-col justify-center"}>
                          <h3 className="text-xl font-medium text-gray-900 group-hover:text-black transition-colors line-clamp-2">
                            {item.name}
                          </h3>

                          {isGrid && item.category?.name && (
                            <p className="mt-2.5 text-sm text-gray-600">
                              {item.category.name}
                            </p>
                          )}

                          <div className="mt-4 group-hover:opacity-100 transition-opacity duration-400">
                            <span className="text-lg font-medium text-gray-700 group-hover:text-black inline-flex items-center gap-1.5">
                              Découvrir Nos Produits
                              <span aria-hidden="true">→</span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
});

export default AllCategory;